import React, { ReactNode, useContext, useEffect, useRef, useState } from "react";
import { MSelect, MSpinner, MSplitter, Map, StandardPinSize, backgroundColor, MultiSelect } from "../Shared"
import { Button, Checkbox } from "react-md";
import { getCounties, getStates, getZipCodes } from "requests/region";
import { AssigneeResponse, CountyDto, InspectorServiceRegionAddRequest, InspectorServiceRegionResponse, InspectorServiceTravelRuleResponse, ServiceRegionDto, StateDto, ZipCodeDto } from "interfaces/Mueller.Business.Models";
import { getInspectorServiceRegions, updateInspectorServiceRegions } from "requests/inspectorServiceRegions";
import { MSelectOption } from "../Shared/MSelect";
import { getInspectorDetails } from "requests/assignment";
import { MapPolygonProps, mapArrayOfPolygonsV2 } from "../Shared/MapHelpers/PolygonHelpers";
import { TooltipProviderValue, TooltipContext } from "../Shared/TooltipProvider";
import { getServiceRegionsDetailed } from "requests/serviceRegions";
import { ServiceTypeEnum } from "typeConstants/ServiceTypeEnum";
import RegionManagementPane from "./RegionManagementPane";
import { DialogContext, DialogProviderValue } from "components/Shared/DialogProvider";
import { getInspectorPinURL } from "components/Shared/Map";
import LoadingCover from "components/Shared/LoadingCover";
import RegionManagementTabs, { RegionManagementTabId, travelRuleFilterOptions } from "./RegionManagementTabs";
import { getInspectorTravelRules } from "requests/inspectorServiceTravelRule";

interface Props {
  inspectorNumber: number,
  onClose?: () => void,
};

// values on this enum matter because they are used to index the mapColors const below
export enum BoundaryType {
  MSA = "MSA",
  County = "County",
  Zip = "Zip",
};

const unselectedOpacity = 0;
const softGreyColor = "#777";
const hardRed = "#F00";

const assignmentFillColor = backgroundColor.button;
const assignmentOpacity = 0.6;

const secondaryLightOpacity = 0.45; // used for secondary regions not selected
const secondaryDarkOpacity = 0.9; // used for secondary regions selected or hovered

interface MapPolyStyle {
  borderColor: string;
  fillColor: string;
  fillOpacity: number;
  borderSize: number;
}

type MapAreaType = "zip" | "county" | "msa";
type MapColors = Record<MapAreaType, MapPolyStyle>;
type OverrideTypes = "primaryCovered" | "secondaryCovered" | "secondaryCoveredSelected";
type MapColorOverrides = Record<MapAreaType, Record<OverrideTypes, Partial<MapPolyStyle>>>;

const mapColors: MapColors = {
  zip: {
    borderColor: hardRed,
    fillColor: assignmentFillColor,
    fillOpacity: unselectedOpacity,
    borderSize: 1,
  },
  county: {
    borderColor: softGreyColor,
    fillColor: assignmentFillColor,
    fillOpacity: unselectedOpacity,
    borderSize: 4,
  },
  msa: {
    borderColor: softGreyColor,
    fillColor: assignmentFillColor,
    fillOpacity: unselectedOpacity,
    borderSize: 4,
  },
};

const mapColorOverrides: MapColorOverrides = {
  zip: {
    primaryCovered: { fillOpacity: assignmentOpacity },
    secondaryCovered: {
      fillColor: softGreyColor,
      fillOpacity: secondaryLightOpacity,
    },
    secondaryCoveredSelected: {
      fillColor: softGreyColor,
      fillOpacity: secondaryDarkOpacity,
    },
  },
  county: {
    primaryCovered: { fillOpacity: assignmentOpacity },
    secondaryCovered: {
      fillColor: softGreyColor,
      fillOpacity: secondaryLightOpacity,
    },
    secondaryCoveredSelected: {
      fillColor: softGreyColor,
      fillOpacity: secondaryDarkOpacity,
    },
  },
  msa: {
    primaryCovered: { fillOpacity: assignmentOpacity },
    secondaryCovered: {
      fillColor: softGreyColor,
      fillOpacity: secondaryLightOpacity,
    },
    secondaryCoveredSelected: {
      fillColor: softGreyColor,
      fillOpacity: secondaryDarkOpacity,
    },
  },
};

interface PolyMeta {
  countyDto?: CountyDto,
  zipDto?: ZipCodeDto,
  msaDto?: ServiceRegionDto,
};

// just the id properties from InspectorServiceRegionResponse
interface RegionIds {
  countyId?: number,
  postalId?: number,
  serviceRegionId?: number,
  travelRuleId: number,
};

export interface ServiceRegionChange {
  changeType: "add" | "remove",
  region: InspectorServiceRegionResponse,
};

export const isValidMsa = (msa: ServiceRegionDto) => msa.countyList?.length > 0 && !!msa.stateAbbreviation;

// only use == here not === because we want null and undefined to match just in case
export const isSameRegion = (r1: RegionIds, r2: RegionIds, skipTravelRuleCheck = false) => r1.countyId == r2.countyId && r1.postalId == r2.postalId && r1.serviceRegionId == r2.serviceRegionId && (r1.travelRuleId == r2.travelRuleId || skipTravelRuleCheck);

const InspectorRegionManager = ({
  inspectorNumber,
  onClose,
}: Props) => {
  const mounted = useRef(true);

  // loading vars (we cant just check that the array has entries because we will lazy load these
  const [isLoadingCountyData, setIsLoadingCountyData] = useState(false);
  const [isLoadingZipData, setIsLoadingZipData] = useState(false);
  const [isSavingRegions, setIsSavingRegions] = useState(false);

  // data state vars
  const [inspectorDetails, setInspectorDetails] = useState<AssigneeResponse>(null);
  const [inspectorServiceRegionsServer, setInspectorServiceRegionsServer] = useState<InspectorServiceRegionResponse[]>(null);
  const [stateList, setStateList] = useState<StateDto[]>([]);
  const [loadedDataForStates, setLoadedDataForStates] = useState<Set<string>>(new Set());
  const [inspectorTravelRules, setInspectorTravelRules] = useState<InspectorServiceTravelRuleResponse[]>();
  const [tabId, setTabId] = useState<RegionManagementTabId>("primaryTab");
  const [showPrimaryCoverage, setShowPrimaryCoverage] = useState(true);
  const [showSecondaryCoverage, setShowSecondaryCoverage] = useState(true);

  useEffect(() => {
    if (tabId === "primaryTab" && !showPrimaryCoverage) setShowPrimaryCoverage(true);
    if (tabId === "secondaryTab" && !showSecondaryCoverage) setShowSecondaryCoverage(true);
  }, [tabId]);

  const [inspectorServiceRegionUpdates, setInspectorServiceRegionUpdates] = useState<ServiceRegionChange[]>([]);

  const [hoveredTravelRuleId, setHoveredTravelRuleId] = useState<number>();
  // this gets updated from the travel rule editing pane, it lets us know on the map side if we are editing regions for a travel rule, or editing regions for the primary coverage
  const [editingTravelRule, _setEditingTravelRule] = useState<InspectorServiceTravelRuleResponse>();
  const setEditingTravelRule = (rule: InspectorServiceTravelRuleResponse, clearOutServiceRegionUpdates: boolean) => {
    _setEditingTravelRule(rule);
    if (clearOutServiceRegionUpdates) setInspectorServiceRegionUpdates([]);
  };

  // these two are lazy loaded, we init array to empty
  const [zipData, setZipData] = useState<ZipCodeDto[]>([]);
  const [countyData, setCountyData] = useState<CountyDto[]>([]);
  // loaded all in one go, init array to null so we know its not loaded
  const [msaData, _setMsaData] = useState<ServiceRegionDto[]>(null); // dont use _setMsaData, use setMsaData because it filters out useless MSAs (MSAs with nothing linked to them because... the system doesnt allow people to delete them)

  const [cachedBoundaryPolys, setCachedBoundaryPolys] = useState<MapPolygonProps<PolyMeta>[]>(null);
  const [visibleBoundaryPolys, setVisibleBoundaryPolys] = useState<MapPolygonProps<PolyMeta>[]>(null);
  
  // ui state vars
  const [showMSABoundaries, setShowMSABoundaries] = useState(true);
  const [showCountyBoundaries, setShowCountyBoundaries] = useState(true);
  const [showZipBoundaries, setShowZipBoundaries] = useState(true);

  const [editTool, setEditTool] = useState(BoundaryType.MSA);

  const [selectedStates, setSelectedStates] = useState<string[]>([]); // abbreviation
  const [travelFilter, setTravelFilter] = useState<MSelectOption>(travelRuleFilterOptions[1]);

  const tooltipContext = useContext<TooltipProviderValue>(TooltipContext);
  const dialogContext = useContext<DialogProviderValue>(DialogContext);

  const setMsaData = (msas: ServiceRegionDto[]) => _setMsaData(msas.filter(isValidMsa));

  let selectedServiceRegions: InspectorServiceRegionResponse[] = null;
  if (inspectorServiceRegionsServer) {
    selectedServiceRegions = inspectorServiceRegionsServer.filter(server => server.travelRuleId == editingTravelRule?.id);
    selectedServiceRegions = selectedServiceRegions.filter(server => !inspectorServiceRegionUpdates.some(update => update.changeType === "remove" && isSameRegion(server, update.region)));
    selectedServiceRegions.push(...inspectorServiceRegionUpdates.filter(update => update.changeType === "add").map(update => update.region));
    selectedServiceRegions.sort((a, b) => a.name.localeCompare(b.name));
  }

  const msaDataFiltered = msaData?.filter(msa => selectedStates.includes(msa.stateAbbreviation));

  const mapStateToDropdown = (state: { abbreviationAndName: string, abbreviation: string }) => ({ label: state.abbreviationAndName, value: state.abbreviation });

  // selects more states in the dropdown by their abbreviation, uses the newest value from state by passing in a function to set state since at times we update this in quick succession
  const selectStates = (states: string[]) => setSelectedStates(selectedStates => Array.from(new Set<string>([...selectedStates, ...states])));

  const loadInspectorRegions = () => {
    setInspectorServiceRegionsServer(null); // let the loading logic know we're loading regions

    getInspectorServiceRegions(inspectorNumber, serviceRegions => {
      let filteredServiceRegions = serviceRegions.filter(s => !!s.stateAbbreviation); // some MSAs are empty, therefor no longer linked to states, this is bad data, we dont want it
      setInspectorServiceRegionsServer(filteredServiceRegions);
      selectStates(filteredServiceRegions.map(sr => sr.stateAbbreviation));
    }, mounted);
  };

  const copyTravelRuleRegionsToSelectedRegions = (rule: InspectorServiceTravelRuleResponse, newTravelRuleId: number) => {
    let newRegionUpdates: ServiceRegionChange[] = inspectorServiceRegionsServer.filter(s => s.travelRuleId === rule.id).map(r => ({ changeType: "add", region: { ...r, travelRuleId: newTravelRuleId }}));
    setInspectorServiceRegionUpdates(newRegionUpdates);
  };

  const loadInspectorTravelRules = () => {
    setInspectorTravelRules(null);
    getInspectorTravelRules({ inspectorNumber, startDate: travelFilter.value && new Date(travelFilter.value) }, setInspectorTravelRules, mounted);
  };

  const loadTravelRulesAndRegions = () => {
    loadInspectorTravelRules();
    loadInspectorRegions();
  };

  useEffect(() => {
    loadInspectorTravelRules();
  }, [travelFilter]);

  // initialize useEffect
  useEffect(() => {
    getStates(setStateList, mounted);

    loadInspectorRegions();

    getInspectorDetails(inspectorNumber, details => {
      setInspectorDetails(details);
      if (details?.inspectorAddress?.stateAbbreviation) selectStates([details.inspectorAddress.stateAbbreviation]);
    }, mounted);

    getServiceRegionsDetailed(ServiceTypeEnum.Inspector, r => setMsaData(r), mounted);

    return () => { mounted.current = false; }
  }, []);

  const getNotLoadedStates = () => selectedStates.filter(s => !loadedDataForStates.has(s));

  const onSave = () => {
    if (inspectorServiceRegionUpdates.length) {
      setIsSavingRegions(true);

      updateInspectorServiceRegions({
        addRequests: inspectorServiceRegionUpdates.filter(u => u.changeType === "add").map<InspectorServiceRegionAddRequest>(u => ({
          countyId: u.region.countyId,
          serviceRegionId: u.region.serviceRegionId,
          postalId: u.region.postalId,
          inspectorNumber: inspectorNumber,
          travelRuleId: null,
        })),
        deleteRequests: inspectorServiceRegionUpdates.filter(u => u.changeType === "remove").map(u => u.region.id),
        inspectorNumber: inspectorNumber,
      }, () => {
        setIsSavingRegions(false);
        setInspectorServiceRegionUpdates([]);
        loadInspectorRegions();
      }, mounted);
    }
  };

  const onCloseWrapped = (areYouSure: boolean = false) => {
    let checkText: string;
    
    if (editingTravelRule) {
      checkText = "You have unsaved changes to a secondary event";
    } else {
      let editCount = inspectorServiceRegionUpdates.length;
      if (editCount > 0) checkText = `You have ${editCount} unsaved change${editCount === 1 ? "" : "s"}`;
    }

    if (areYouSure === true || !checkText) onClose();
    else dialogContext.showYesNo("Are you sure you want to exit?", checkText, () => onCloseWrapped(true));
  };

  // get county and zip data based on when the state list loads, selected states are changed, or inspector details or regions change
  useEffect(() => {
    // make sure data is loaded before we call out to get boundaries
    if (inspectorDetails && inspectorServiceRegionsServer && stateList.length && selectedStates.length) {
      // see if theres any new data to load for counties
      let needToLoadStates = getNotLoadedStates();
      setLoadedDataForStates(currentlyLoaded => {
        let newLoaded = new Set(currentlyLoaded);
        needToLoadStates.forEach(s => newLoaded.add(s));
        return newLoaded;
      });

      if (needToLoadStates.length) {
        setIsLoadingCountyData(true);
        getCounties({ stateAbbrevation: needToLoadStates, includeBoundaries: true }, counties => {
          setCountyData(existing => [...existing, ...counties.filter(c => c.boundary)]);
          setIsLoadingCountyData(false);
        }, mounted);

        setIsLoadingZipData(true);
        getZipCodes({ stateAbbrevation: needToLoadStates, includeBoundaries: true }, zips => {
          setZipData(existing => [...existing, ...zips.filter(z => z.boundary)]);
          setIsLoadingZipData(false);
        }, mounted);
      }
    }
  }, [inspectorDetails, inspectorServiceRegionsServer, stateList, selectedStates]);
  
  // only count a region as selected if it is in selectedServiceRegions and also it matches the travel rule we are editing
  const isRegionSelectedForEditing = (region: RegionIds) => selectedServiceRegions.some(
    r => isSameRegion(r, region) &&
    r.travelRuleId == editingTravelRule?.id // only use == here not === because we want null and undefined to match 
  );
  
  const getPolyStyle = (
    boundaryType: BoundaryType,
    regionStyle: ReturnType<typeof getRegionSelectionData>,
  ): MapPolyStyle => {
    // Convert enum BoundaryType => "msa" | "county" | "zip"
    const areaType = boundaryType.toLowerCase() as MapAreaType;
  
    let mergeStyle: Partial<MapPolyStyle> = null; // dont initialize to empty object because we rely on falsey value for logic

    if (regionStyle.isSelectedForEditing) {
      if (editingTravelRule) {
        mergeStyle = mapColorOverrides[areaType].secondaryCoveredSelected;
      } else {
        // we can get here and still want to filter out primary if we are on the secondary tab, but not editing something
        // then the region will be highlighted because it is selected for editing and we are not editing a travel rule
        if (showPrimaryCoverage) mergeStyle = mapColorOverrides[areaType].primaryCovered;
      }
    }
    
    if (!mergeStyle) {
      if (regionStyle.isPrimary && showPrimaryCoverage && regionStyle.isSelectedForEditing) {
        mergeStyle = mapColorOverrides[areaType].primaryCovered;
      } else if (regionStyle.isSecondary && showSecondaryCoverage) {
        if (regionStyle.isSecondaryHovered) {
          mergeStyle = mapColorOverrides[areaType].secondaryCoveredSelected;
        } else {
          mergeStyle = mapColorOverrides[areaType].secondaryCovered;
        }
      }
    }
  
    return { ...mapColors[areaType], ...mergeStyle };
  };

  const getRegionSelectionData = (region: RegionIds) => {
    let matchingRegions = inspectorServiceRegionsServer.filter(r => isSameRegion(r, region, true));

    return {
      isSelectedForEditing: isRegionSelectedForEditing(region),
      isPrimary: matchingRegions.some(r => !r.travelRuleId),
      // checking if the list of travel rules that we are currently showing has this region, since the list of rules might be filtered out, but the region data always has all data
      isSecondary: matchingRegions.some(r => r.travelRuleId !== null && inspectorTravelRules?.some(tr => tr.id == r.travelRuleId)),
      isSecondaryHovered: matchingRegions.some(r => r.travelRuleId === hoveredTravelRuleId),
    };
  }

  // updated our cached polygons based on new zip or county data being loaded
  useEffect(() => {
    if (!isLoadingCountyData || !isLoadingZipData && msaData) {
      let polys = mapArrayOfPolygonsV2<PolyMeta>(countyData.map(c => {
        let regionData = getRegionSelectionData({ countyId: c.id, travelRuleId: editingTravelRule?.id });
        let polyStyle = getPolyStyle(BoundaryType.County, regionData);

        return {
          id: `county-${c.id}`,
          pathJson: c.boundary,
          zIndex: 1,
          style: {
            fillColor: polyStyle.fillColor,
            fillOpacity: polyStyle.fillOpacity,
            strokeColor: polyStyle.borderColor,
            strokeWeight: polyStyle.borderSize,
          },
          meta: {
            countyDto: c,
          },
        };
      }));

      polys.push(...mapArrayOfPolygonsV2<PolyMeta>(zipData.map(z => {
        let regionStyle = getRegionSelectionData({ postalId: z.id, travelRuleId: editingTravelRule?.id });
        let polyStyle = getPolyStyle(BoundaryType.Zip, regionStyle);

        return {
          id: `zip-${z.zipCode}`,
          pathJson: z.boundary,
          zIndex: 2,
          style: {
            fillColor: polyStyle.fillColor,
            fillOpacity: polyStyle.fillOpacity,
            strokeColor: polyStyle.borderColor,
            strokeWeight: polyStyle.borderSize,
          },
          meta: {
            zipDto: z,
          },
        };
      })));

      setCachedBoundaryPolys(polys);
    }
  
  }, [zipData, countyData, msaData, isLoadingZipData, isLoadingCountyData]);

  const getMouseOverText = (polyMeta: PolyMeta, isSelected: boolean) => {
    let boundaryType = getBoundaryTypeFromPolyMeta(polyMeta);
    let tipText: string;

    switch (boundaryType) {
      case BoundaryType.MSA:
        tipText = `${polyMeta.msaDto.name} (MSA)`;
        break;
      case BoundaryType.County:
        let msa = getLinkedMSA(polyMeta);
        tipText = `${polyMeta.countyDto.name} County, ${polyMeta.countyDto.state}`;
        if (msa) tipText = `${tipText} (${msa.name})`;
        break;
      case BoundaryType.Zip:
        tipText = `${polyMeta.zipDto.zipCode}, ${polyMeta.zipDto.stateAbbreviation} (${polyMeta.zipDto.county} County)`;
        break;
    };

    if (isSelected) tipText = `Selected: ${tipText}`;
    return tipText;
  };

  const getSelectedViewBoundaryOptions = () => 
    [showMSABoundaries && BoundaryType.MSA, showCountyBoundaries && BoundaryType.County, showZipBoundaries && BoundaryType.Zip]
    .filter(s => s);

  const onViewBoundaryOptionsChanged = (options: BoundaryType[]) => {
    let showMSA = false;
    let showCounty = false;
    let showZip = false;

    options.forEach(o =>{
      if (o === BoundaryType.MSA) showMSA = true;
      if (o === BoundaryType.County) showCounty = true;
      if (o === BoundaryType.Zip) showZip = true;
    });

    if (editTool === BoundaryType.MSA && !showMSA) setEditTool(null);
    if (editTool === BoundaryType.County && !showCounty) setEditTool(null);
    if (editTool === BoundaryType.Zip && !showZip) setEditTool(null);

    setShowMSABoundaries(showMSA);
    setShowCountyBoundaries(showCounty);
    setShowZipBoundaries(showZip);
  };

  const getSelectedEditBoundaryOptions = () => editTool ? [editTool] : [];

  const onEditBoundaryOptionsChanged = (options: BoundaryType[]) => {
    if (!options?.length) {
      setEditTool(null);
    } else {
      let newTool = options.filter(o => o !== editTool)[0];
      setEditTool(newTool);
      if (newTool === BoundaryType.MSA) setShowMSABoundaries(true);
      if (newTool === BoundaryType.County) setShowCountyBoundaries(true);
      if (newTool === BoundaryType.Zip) setShowZipBoundaries(true);

    }
  };

  const getEditBoundaryLabel = () => {
    switch (editTool) {
      case BoundaryType.MSA:
        return "Editing MSAs";
      case BoundaryType.County:
        return "Editing Counties";
      case BoundaryType.Zip:
        return "Editing Zips";
      default:
        return "Update Boundary To Edit";
    }
  };

  const getBoundaryTypeFromPolyMeta = (meta: PolyMeta) => {
    if (meta.msaDto) return BoundaryType.MSA;
    if (meta.countyDto) return BoundaryType.County;
    if (meta.zipDto) return BoundaryType.Zip;
    return null;
  };

  const getLinkedMSA = (meta: PolyMeta) => {
    if (meta.msaDto) return meta.msaDto;
    else if (msaData && meta.countyDto) return msaData?.find(msa => msa.countyList.some(c => c.id === meta.countyDto?.id));
    else return null;
  };

  // update the visble boundaries based on selected regions, selected states, and cached poly boundaries
  useEffect(() => {
    const boundaryOrder = [BoundaryType.MSA, BoundaryType.County, BoundaryType.Zip];

    // make sure we are loaded
    if (cachedBoundaryPolys?.length && inspectorServiceRegionsServer) {
      let visibleBoundaries = cachedBoundaryPolys.filter(cached => selectedStates.includes(cached.meta.countyDto?.state ?? cached.meta.zipDto?.stateAbbreviation));
      
      // duplicate county polygons to also make MSA polygons
      let msaPolys = visibleBoundaries.filter(b => getBoundaryTypeFromPolyMeta(b.meta) === BoundaryType.County).map(p => {
        let newPoly = { ...p };

        let msa = getLinkedMSA(newPoly.meta);
        if (msa) {
          newPoly.id = `msa-${msa.id}-county${p.meta.countyDto.id}`
          newPoly.meta = { msaDto: msa };
          return newPoly;
        } else {
          return null;
        }
      }).filter(p => !!p);

      visibleBoundaries.push(...msaPolys);

      let newVisible = visibleBoundaries.map(p => {
        let boundaryType = getBoundaryTypeFromPolyMeta(p.meta);
        let newPoly = { ...p };

        let regionStyle = getRegionSelectionData({ countyId: p.meta.countyDto?.id, postalId: p.meta.zipDto?.id, serviceRegionId: p.meta.msaDto?.id, travelRuleId: editingTravelRule?.id });
        let polyStyle = getPolyStyle(boundaryType, regionStyle);

        if (boundaryType === BoundaryType.MSA && !showMSABoundaries) return null;
        if (boundaryType === BoundaryType.County && !showCountyBoundaries) return null;
        if (boundaryType === BoundaryType.Zip && !showZipBoundaries) return null;

        newPoly.fillColor = polyStyle.fillColor;
        newPoly.fillOpacity = polyStyle.fillOpacity;
        newPoly.strokeColor = polyStyle.borderColor;
        newPoly.strokeWeight = polyStyle.borderSize;

        newPoly.onMouseOver = () => tooltipContext.show(getMouseOverText(p.meta, regionStyle.isSelectedForEditing));
        newPoly.onMouseOut = () => tooltipContext.hide();

        let isEditingType = boundaryType === editTool;

        // sort polygon boundaries so whatever we are editing sorts to the top
        newPoly.zIndex = isEditingType ? boundaryOrder.length : boundaryOrder[boundaryType];

        if (editTool === boundaryType) newPoly.onClick = () => toggleRegionSelected(p.meta, true);
        return newPoly;
      }).filter(b => !!b);
      
      setVisibleBoundaryPolys(newVisible);
    }
  }, [
    inspectorServiceRegionsServer,
    inspectorServiceRegionUpdates,
    cachedBoundaryPolys,
    selectedStates,
    showCountyBoundaries,
    showZipBoundaries,
    showMSABoundaries,
    editTool,
    editingTravelRule,
    hoveredTravelRuleId,
    showPrimaryCoverage,
    showSecondaryCoverage,
    inspectorTravelRules,
  ]);

  const getSelectedStates = (): MSelectOption[] => {
    if (stateList?.length) {
      return selectedStates.map(s => ({ label: stateList.find(sl => sl.abbreviation === s).abbreviationAndName, value: s }));
    } else {
      return [];
    }
  };

  const getDisplayNameForDto = ({ countyDto, msaDto, zipDto }: PolyMeta) => 
    (countyDto && `${countyDto.name}, ${countyDto.state}`) ||
    (zipDto && `${zipDto.zipCode}, ${zipDto.stateAbbreviation}`) ||
    (msaDto && msaDto.name);

  const toggleRegionSelected = (polyMeta: PolyMeta, toggledFromPolygon: boolean = false) => {
    // this gets called multiple times when editing MSA in bulk so we need to get the latest region updates from the set state func
    setInspectorServiceRegionUpdates(inspectorServiceRegionUpdates => {
      // dont use poly meta after this unwrapping. use these variables instead because they might get modified since our msa/county logic is kinda whack
      let { countyDto, zipDto, msaDto } = polyMeta;
      let newServiceRegionUpdates = [...inspectorServiceRegionUpdates];

      let isSelected = isRegionSelectedForEditing({ countyId: countyDto?.id, postalId: zipDto?.id, serviceRegionId: msaDto?.id, travelRuleId: editingTravelRule?.id });

      if (toggledFromPolygon) tooltipContext.show(getMouseOverText(polyMeta, !isSelected));

      let newRegion = {
        countyId: countyDto?.id,
        postalId: zipDto?.id,
        serviceRegionId: msaDto?.id,
        stateAbbreviation: countyDto?.state ?? zipDto?.stateAbbreviation ?? msaDto?.stateAbbreviation,
        name: getDisplayNameForDto(polyMeta),
        travelRuleId: editingTravelRule?.id,
      } as InspectorServiceRegionResponse;

      if (isSelected) {
        // check if the update array has an add entry, if so remove it
        // otherwise make a remove entry for it in the update array
        let regionToRemove = inspectorServiceRegionUpdates.find(update => update.changeType === "add" && isSameRegion(newRegion, update.region));

        if (regionToRemove) {
          newServiceRegionUpdates = newServiceRegionUpdates.filter(r => r !== regionToRemove);
        } else {
          // if we are adding a remove entry to the array, that means the server has a record of what we are removing, we need to get its id in case the user saves this remove action
          let serverRegion = inspectorServiceRegionsServer.find(s => isSameRegion(s, newRegion));
          newRegion.id = serverRegion.id;
          newServiceRegionUpdates.push({
            changeType: "remove",
            region: newRegion,
          });
        }
      } else {
        // check if the update array has a remove entry, if so remove it
        // otherwise make an add entry for it in the update array
        let regionToRemove = inspectorServiceRegionUpdates.find(update => update.changeType === "remove" && isSameRegion(newRegion, update.region));

        if (regionToRemove) {
          newServiceRegionUpdates = newServiceRegionUpdates.filter(r => r !== regionToRemove);
        } else {
          newServiceRegionUpdates.push({
            changeType: "add",
            region: newRegion,
          });
        }
      }

      return newServiceRegionUpdates;
    });
  };

  let loadingMessages: string[] = [];

  if (!inspectorDetails) loadingMessages.push("Loading inspector data...");
  if (!stateList.length) loadingMessages.push("Loading state list...");
  if (isLoadingZipData) loadingMessages.push("Loading zip data...");
  if (isLoadingCountyData) loadingMessages.push("Loading county data...");
  if (!msaData) loadingMessages.push("Loading MSA data...");
  if (!inspectorServiceRegionsServer) loadingMessages.push("Loading existing inspector service regions...");

  if (isSavingRegions) {
    loadingMessages.push(...inspectorServiceRegionUpdates.map(u => `${u.changeType === "add" ? "Adding " : "Removing "} ${u.region.name}`));
  }

  let isLoading = !!loadingMessages.length;

  let inspectorLocation: { lat: number, lng: number };
  if (inspectorDetails?.inspectorLocation?.latitude && inspectorDetails?.inspectorLocation?.longitude) inspectorLocation = { lat: inspectorDetails.inspectorLocation.latitude, lng: inspectorDetails.inspectorLocation.longitude };

  let mapPins = [];
  if (inspectorDetails?.inspectorLocation?.latitude && inspectorDetails?.inspectorLocation?.longitude) {
    mapPins.push({
      id: "inspector-pin",
      latitude: inspectorDetails.inspectorLocation.latitude,
      longitude: inspectorDetails.inspectorLocation.longitude,
      ...StandardPinSize,
      iconURL: getInspectorPinURL(false, false),
    });
  }

  const editorPane = (
    <div className="editor-pane">
      {isLoading && <LoadingCover loadingMessages={loadingMessages} />}
      <div className="options">
        <div className="state-select">
          Show Boundaries for the following states
          <MSelect
            isMulti
            enableAllOption={false}
            placeholder="Select States..."
            className="select-editor"
            options={stateList.map(mapStateToDropdown)}
            onChange={selected => setSelectedStates(selected.map(s => s.value))}
            value={getSelectedStates()}
          />
        </div>
        <MultiSelect
          disableSearch
          options={[
            { label: "Show MSAs", value: BoundaryType.MSA },
            { label: "Show Counties", value: BoundaryType.County },
            { label: "Show Zips", value: BoundaryType.Zip },
          ]}
          selected={getSelectedViewBoundaryOptions()}
          onSelectedChanged={onViewBoundaryOptionsChanged}
          hasSelectAll={false}
          valueRenderer={() => "Update Boundary to Display"}
        />
        <MultiSelect
          disableSearch
          options={[
            { label: "Edit MSAs", value: BoundaryType.MSA },
            { label: "Edit Counties", value: BoundaryType.County },
            { label: "Edit Zips", value: BoundaryType.Zip },
          ]}
          selected={getSelectedEditBoundaryOptions()}
          onSelectedChanged={onEditBoundaryOptionsChanged}
          hasSelectAll={false}
          valueRenderer={getEditBoundaryLabel}
        />
        {tabId === "primaryTab" &&
        <Checkbox
          className="show-hide-coverage"
          id="showSecondaryCoverage"
          name="showSecondaryCoverage"
          label="Show Secondary Coverage"
          onChange={setShowSecondaryCoverage}
          checked={showSecondaryCoverage}
        />}
        {tabId === "secondaryTab" &&
        <Checkbox
          className="show-hide-coverage"
          id="showPrimaryCoverage"
          name="showPrimaryCoverage"
          label="Show Primary Coverage"
          onChange={setShowPrimaryCoverage}
          checked={showPrimaryCoverage}
        />}
      </div>
      {inspectorDetails &&
        <Map
          center={inspectorLocation}
          pins={mapPins}
          polygons={visibleBoundaryPolys}
          zoom={10}
        />
      }
    </div>
  );

  const saveEditedMSAList = (oldSelected: number[], newSelected: number[]) => {
    let changes = [
      ...oldSelected.filter(i => !newSelected.includes(i)),
      ...newSelected.filter(i => !oldSelected.includes(i)),
    ];

    changes.forEach(msaI => toggleRegionSelected({ msaDto: msaDataFiltered[msaI] }));
  };

  const handleDelete = (row: InspectorServiceRegionResponse) => {
    let removeType: string = null;
    if (row.countyId && !countyData?.length) removeType = "county";
    if (row.postalId && !zipData?.length) removeType = "zip code";
    if (row.serviceRegionId && !msaData?.length) removeType = "MSA";

    if (removeType) {
      dialogContext.showOk("Still Loading Data...", `You can't remove this ${removeType} until all of the ${removeType} data is loaded`);
      return;
    }

    toggleRegionSelected({
      countyDto: row.countyId && countyData.find(c => c.id === row.countyId),
      zipDto: row.postalId && zipData.find(z => z.id === row.postalId),
      msaDto: row.serviceRegionId && msaData.find(m => m.id === row.serviceRegionId),
    });
  };

  // because of how the grid renders, we need to make a ref so it can call the newest version of handleDelete... functional components honestly suck
  const handleDeleteRef = useRef(handleDelete);
  handleDeleteRef.current = handleDelete;

  let saveButtonText = "No Changes to Save";
  if (isSavingRegions) saveButtonText = "Saving Changes...";
  else if (inspectorServiceRegionUpdates.length > 0 && !editingTravelRule) saveButtonText = `Save ${inspectorServiceRegionUpdates.length} Change${inspectorServiceRegionUpdates.length === 1 ? "" : "s"}`;

  let editText = "Editing Field Rep Coverage";

  if (inspectorDetails) editText += ` for ${inspectorDetails.inspectorLocation.displayName}`;

  return (
    <div className="region-selector-container">
      <div className="title">
        <div className="title-text">{editText}</div>
        <Button raised primary onClick={onSave} disabled={inspectorServiceRegionUpdates.length === 0 || isSavingRegions || !!editingTravelRule}>{saveButtonText}</Button>
        {onClose && <Button raised primary onClick={() => onCloseWrapped()}>Close Coverage Manager</Button>}
      </div>
      <MSplitter
        firstChild={editorPane}
        firstChildInitialSize="75%"
        secondChild={
          <RegionManagementTabs
            inspectorNumber={inspectorNumber}
            onDeleteRef={handleDeleteRef}
            selectedServiceRegions={selectedServiceRegions}
            inspectorServiceRegionUpdates={inspectorServiceRegionUpdates}
            travelRules={inspectorTravelRules}
            availableMSAList={msaDataFiltered}
            isLoading={isSavingRegions}
            onMSAEditsSaved={saveEditedMSAList}
            isSavingRegions={isSavingRegions}
            reloadTravelRulesAndRegions={loadTravelRulesAndRegions}
            setEditingTravelRule={setEditingTravelRule}
            copyTravelRuleRegions={copyTravelRuleRegionsToSelectedRegions}
            travelFilter={travelFilter}
            setTravelFilter={setTravelFilter}
            onTravelRuleHover={(rule: InspectorServiceTravelRuleResponse) => setHoveredTravelRuleId(rule?.id)}
            onTabChanged={setTabId}
          />}
      />
    </div>
  );
};

export default InspectorRegionManager;
